import { loadStripe } from '@stripe/stripe-js';
import { apiRequest } from './api';

// Make sure to call loadStripe outside of a component's render
let stripePromise: ReturnType<typeof loadStripe>;

// Initialize Stripe with the public key from environment variables
export const getStripe = () => {
  if (!stripePromise) {
    if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
      throw new Error('Missing Stripe public key. Please set VITE_STRIPE_PUBLIC_KEY in your environment.');
    }
    stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);
  }
  return stripePromise;
};

// Create a payment intent for a booking
export async function createPaymentIntent(bookingId: number) {
  try {
    const response = await apiRequest('POST', 'create-payment-intent', { bookingId });
    
    if (!response.clientSecret) {
      throw new Error('Failed to create payment intent');
    }
    
    return { clientSecret: response.clientSecret };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    return { error: { message: error instanceof Error ? error.message : 'Failed to create payment intent' } };
  }
}

// Create a Stripe Connect account for a user (owner or agent)
export async function createStripeAccount() {
  try {
    const response = await apiRequest('POST', 'stripe/account');
    
    if (!response || !response.url) {
      throw new Error('Failed to create Stripe account');
    }
    
    // Extract the account ID from the URL or response
    const accountId = response.accountId || extractAccountId(response.url);
    
    return { 
      accountLink: response.url,
      accountId
    };
  } catch (error) {
    console.error('Error creating Stripe account:', error);
    return { error: { message: error instanceof Error ? error.message : 'Failed to create Stripe account' } };
  }
}

// Get the status of a user's Stripe Connect account
export async function getStripeAccountStatus() {
  try {
    const response = await apiRequest('GET', 'stripe/account-status');
    
    if (!response) {
      throw new Error('Failed to get account status');
    }
    
    if (!response.hasAccount) {
      return null;
    }
    
    return response.accountStatus;
  } catch (error) {
    console.error('Error getting Stripe account status:', error);
    return { error: { message: error instanceof Error ? error.message : 'Failed to get account status' } };
  }
}

// Create a link to the Stripe dashboard for a user
export async function createDashboardLink() {
  try {
    const response = await apiRequest('POST', 'stripe/dashboard-link');
    
    if (!response || !response.url) {
      throw new Error('Failed to create dashboard link');
    }
    
    return { url: response.url };
  } catch (error) {
    console.error('Error creating dashboard link:', error);
    return { error: { message: error instanceof Error ? error.message : 'Failed to create dashboard link' } };
  }
}

// Helper function to extract account ID from a URL
function extractAccountId(url: string): string {
  // This is a simplified helper that assumes the account ID is in the URL
  // In a real implementation, the server should return the account ID directly
  const match = url.match(/acct_[a-zA-Z0-9]+/);
  return match ? match[0] : '';
}